import * as React from 'react'
import {
  ArrowRightLeft,
  ArrowUpRight,
  Ban,
  CircleX,
  Download,
  Loader2,
  MoreHorizontal,
  Play,
  RotateCcw,
  SkipForward,
  Square
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  downloadIntakeItemFile,
  fetchIntakeItemFile,
  useAcknowledgeTabularIngestJobMutation,
  useConfirmIngestIntakeItemMutation,
  useLazyListIngestIntakeItemsQuery,
  useRunIngestIntakeItemMutation,
  useSkipIngestIntakeItemMutation,
  useStopIngestJobMutation,
  type IngestIntake,
  type IngestIntakeItem
} from '@axilon-apps/domains-ontology-ui-state'

import { readMutationError } from '../../../components/explorer'
import { StatusPill } from '../../../components/primitives/StatusPill'
import { ReplaceConfirmDialog } from '../../../components/ReplaceConfirmDialog'
import { SourceDocViewer } from '../../../components/SourceDocViewer/SourceDocViewer'
import { useSourceDocViewer } from '../../../components/SourceDocViewer/useSourceDocViewer'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../../components/ui/alert-dialog'
import { Button } from '../../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu'
import { replacementRequestOf, type ReplacementRequest } from '../../../lib/replaced-files'
import { formatRelativeTime } from '../../../lib/time'
import { formatBytes } from '../../DocsHubPage/doc-helpers'
import { tabularJobPath } from '../../TabularIngestJobPage/route'
import { NoteDialog } from '../shared/NoteDialog'
import { intakesTabPath, statusPill } from './intake-view'

interface IntakeLedgerProps {
  intake: IngestIntake
}

type RowAction = 'run' | 'skip' | 'cancel' | 'replace'

interface PendingConfirm {
  action: 'skip' | 'cancel' | 'replace'
  item: IngestIntakeItem
}

const HEADER_CLASS = 'py-2 pr-3 text-10 font-medium tracking-[0.06em] uppercase'
const ACTIVE_REFRESH_INTERVAL_MS = 2_000
const LINKED_PILL_STATUSES = new Set<IngestIntakeItem['status']>([
  'failed',
  'awaiting_rebuild',
  'awaiting_review',
  'awaiting_turn'
])

export function IntakeLedger({ intake }: IntakeLedgerProps) {
  const intakeId = intake.id
  const backTo = intakesTabPath(intakeId)
  const [loadItems, loadState] = useLazyListIngestIntakeItemsQuery()
  const [runItem] = useRunIngestIntakeItemMutation()
  const [skipItem] = useSkipIngestIntakeItemMutation()
  const [confirmItem] = useConfirmIngestIntakeItemMutation()
  const [stopJob] = useStopIngestJobMutation()
  const [acknowledge, acknowledgeState] = useAcknowledgeTabularIngestJobMutation()
  const [items, setItems] = React.useState<IngestIntakeItem[]>([])
  const [nextBefore, setNextBefore] = React.useState<string | null>(null)
  const [error, setError] = React.useState('')
  const [busyItemId, setBusyItemId] = React.useState<string | null>(null)
  const [confirm, setConfirm] = React.useState<PendingConfirm | null>(null)
  const [replacing, setReplacing] = React.useState<
    (ReplacementRequest & { item: IngestIntakeItem }) | null
  >(null)
  const [ignoring, setIgnoring] = React.useState<IngestIntakeItem | null>(null)
  const [ignoreNote, setIgnoreNote] = React.useState('')
  const [ignoreError, setIgnoreError] = React.useState<string>()
  const { openSource, viewerProps } = useSourceDocViewer()

  const loadFirstPage = React.useCallback(
    async (merge = false) => {
      setError('')
      try {
        const page = await loadItems({ id: intakeId, limit: 50 }, false).unwrap()
        setItems((current) => {
          if (!merge) return page.items
          const refreshed = new Set(page.items.map((item) => item.itemId))
          return [...page.items, ...current.filter((item) => !refreshed.has(item.itemId))]
        })
        if (!merge) setNextBefore(page.nextBefore)
      } catch (loadError) {
        setError(readMutationError(loadError))
      }
    },
    [intakeId, loadItems]
  )

  React.useEffect(() => {
    setItems([])
    setNextBefore(null)
    void loadFirstPage()
  }, [loadFirstPage])

  const startsOnItsOwn = hasTabularTarget(intake) && intake.enabled
  const hasActiveItems = items.some(
    (item) => item.status === 'running' || (item.status === 'pending' && startsOnItsOwn)
  )
  // Intake items publish no wake-up, so active items stay on a deliberate poll.
  React.useEffect(() => {
    if (!hasActiveItems) return
    const interval = window.setInterval(() => {
      void loadFirstPage(true)
    }, ACTIVE_REFRESH_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [hasActiveItems, loadFirstPage])

  const loadMore = async () => {
    if (!nextBefore) return
    setError('')
    try {
      const page = await loadItems({ id: intakeId, limit: 50, before: nextBefore }, false).unwrap()
      setItems((current) => {
        const seen = new Set(current.map((item) => item.itemId))
        return [...current, ...page.items.filter((item) => !seen.has(item.itemId))]
      })
      setNextBefore(page.nextBefore)
    } catch (loadError) {
      setError(readMutationError(loadError))
    }
  }

  const act = async (action: RowAction, item: IngestIntakeItem, replaceExisting = false) => {
    setBusyItemId(item.itemId)
    try {
      if (action === 'skip') {
        const skipped = await skipItem({ itemId: item.itemId }).unwrap()
        setItems((current) => current.map((row) => (row.itemId === skipped.itemId ? skipped : row)))
        toast.success(
          `${item.status === 'awaiting_confirmation' ? 'Discarded' : 'Skipped'} ${item.fileName}`
        )
        return
      }
      if (action === 'replace') {
        const queued = await confirmItem({ itemId: item.itemId }).unwrap()
        setItems((current) => current.map((row) => (row.itemId === queued.itemId ? queued : row)))
        toast.success(`Queued ${item.fileName} to replace the earlier file's rows`)
        void loadFirstPage(true)
        return
      }
      if (action === 'cancel' && item.jobId) {
        await stopJob(item.jobId).unwrap()
        toast.success(`Stopping ${item.fileName}`)
      } else if (action === 'run') {
        await runItem({
          itemId: item.itemId,
          force: true,
          ...(replaceExisting ? { replaceExisting: true } : {})
        }).unwrap()
        toast.success(`Started ${item.fileName}`)
      }
      void loadFirstPage(true)
    } catch (actionError) {
      const replacement = action === 'run' ? replacementRequestOf(actionError) : null
      if (replacement) {
        setReplacing({ ...replacement, item })
        return
      }
      toast.error(rowActionError(actionError))
      void loadFirstPage(true)
    } finally {
      setBusyItemId(null)
      setConfirm(null)
    }
  }

  const openIgnore = (item: IngestIntakeItem) => {
    setIgnoreNote('')
    setIgnoreError(undefined)
    setIgnoring(item)
  }

  const ignoreFile = async () => {
    if (!ignoring?.jobId) return
    const note = ignoreNote.trim()
    try {
      await acknowledge({ jobId: ignoring.jobId, ...(note ? { note } : {}) }).unwrap()
      toast.success(`Ignored ${ignoring.fileName}`)
      setIgnoring(null)
      void loadFirstPage(true)
    } catch (acknowledgeError) {
      setIgnoreError(readMutationError(acknowledgeError))
    }
  }

  const download = (item: IngestIntakeItem) => {
    downloadIntakeItemFile(item.itemId, item.fileName).catch((downloadError: unknown) =>
      toast.error(
        downloadError instanceof Error
          ? downloadError.message
          : `${item.fileName} could not be downloaded.`
      )
    )
  }

  const preview = (item: IngestIntakeItem) =>
    openSource({
      sourceFile: item.fileName,
      supportsOriginalDownload: false,
      loadSource: () => fetchIntakeItemFile(item.itemId)
    })

  const blockers = olderPendingByItem(items)

  const heading = (
    <div className="text-11 font-medium tracking-[0.05em] text-ink-3 uppercase">
      Received files
      {items.length > 0 ? ` · ${items.length}${nextBefore ? '+' : ''}` : ''}
    </div>
  )

  if (loadState.isFetching && items.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {heading}
        <div className="flex items-center gap-2 rounded-md border border-line bg-bg-elev px-3 py-3 text-xs text-ink-3">
          <Loader2 className="size-3.5 animate-spin" />
          Loading received files…
        </div>
      </div>
    )
  }

  if (error && items.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {heading}
        <div className="flex items-center justify-between gap-3 rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
          <span role="alert">{error}</span>
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="shrink-0"
            onClick={() => void loadFirstPage()}
          >
            Try again
          </Button>
        </div>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {heading}
        <div className="rounded-md border border-dashed border-line px-3 py-3 text-xs text-ink-3">
          No files received yet. Push one to the URL above and it will appear here.
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {heading}
      <div className="overflow-x-auto rounded-md border border-line bg-bg-elev">
        <table className="w-full min-w-180 table-fixed text-left">
          <colgroup>
            <col className="w-24" />
            <col />
            <col className="w-18" />
            <col className="w-42.5" />
            <col className="w-24" />
            <col className="w-37.5" />
            <col className="w-10" />
          </colgroup>
          <thead className="border-b border-line text-ink-3">
            <tr>
              <th className={`px-3 ${HEADER_CLASS}`}>Received</th>
              <th className={HEADER_CLASS}>File</th>
              <th className={HEADER_CLASS}>Size</th>
              <th className={HEADER_CLASS}>Status</th>
              <th className={HEADER_CLASS}>Job</th>
              <th className={HEADER_CLASS}>Sent by</th>
              <th className={HEADER_CLASS}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <LedgerRow
                key={item.itemId}
                item={item}
                intake={intake}
                backTo={backTo}
                olderPending={blockers.get(item.itemId)}
                busy={busyItemId === item.itemId}
                onRun={(row) => void act('run', row)}
                onConfirm={setConfirm}
                onIgnore={openIgnore}
                onDownload={download}
                onPreview={preview}
              />
            ))}
          </tbody>
        </table>
      </div>
      {error || nextBefore ? (
        <div className="flex items-center justify-between gap-3">
          {error ? (
            <p role="alert" className="text-xs text-st-down">
              {error}
            </p>
          ) : (
            <span />
          )}
          {nextBefore ? (
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={loadState.isFetching}
              onClick={() => void loadMore()}
            >
              {loadState.isFetching ? <Loader2 className="size-3 animate-spin" /> : null}
              Load more
            </Button>
          ) : null}
        </div>
      ) : null}
      <ConfirmRowActionDialog
        confirm={confirm}
        busy={confirm !== null && busyItemId === confirm.item.itemId}
        onOpenChange={(open) => {
          if (!open && busyItemId === null) setConfirm(null)
        }}
        onConfirm={(pending) => void act(pending.action, pending.item)}
      />
      <ReplaceConfirmDialog
        open={replacing !== null}
        onOpenChange={(open) => {
          if (!open) setReplacing(null)
        }}
        configName={replacing?.configName ?? ''}
        files={replacing?.files ?? []}
        onConfirm={() => {
          if (!replacing) return
          setReplacing(null)
          void act('run', replacing.item, true)
        }}
      />
      <NoteDialog
        open={ignoring !== null}
        onOpenChange={(open) => {
          if (!open && !acknowledgeState.isLoading) setIgnoring(null)
        }}
        title="Mark this as a bad file?"
        description="The failed run stays in history, marked ignored, and stops counting toward this intake's failed files. You can still run the file again."
        note={ignoreNote}
        onNoteChange={setIgnoreNote}
        actionLabel="Ignore bad file"
        loading={acknowledgeState.isLoading}
        error={ignoreError}
        onSubmit={() => void ignoreFile()}
      />
      <SourceDocViewer {...viewerProps} />
    </div>
  )
}

function LedgerRow({
  item,
  intake,
  backTo,
  olderPending,
  busy,
  onRun,
  onConfirm,
  onIgnore,
  onDownload,
  onPreview
}: {
  item: IngestIntakeItem
  intake: IngestIntake
  /** Where the job page's Back returns: this intake, expanded. */
  backTo: string
  /** The oldest loaded file received before this one that is still pending. */
  olderPending?: IngestIntakeItem
  busy: boolean
  onRun: (item: IngestIntakeItem) => void
  onConfirm: (confirm: PendingConfirm) => void
  onIgnore: (item: IngestIntakeItem) => void
  onDownload: (item: IngestIntakeItem) => void
  onPreview: (item: IngestIntakeItem) => void
}) {
  const status = statusPill(item, intake)
  const tabular = hasTabularTarget(intake)
  const runLabel =
    item.status === 'pending' && tabular && !intake.enabled
      ? 'Run now'
      : item.status === 'failed' && tabular
        ? 'Run again'
        : item.status === 'skipped' && tabular
          ? 'Run anyway'
          : null
  const held = item.status === 'awaiting_confirmation'
  const canSkip = item.status === 'pending' || held
  const canIgnore = item.status === 'failed' && !item.acknowledged && item.jobId !== null
  const canCancel = item.status === 'running' && item.jobId !== null

  return (
    <tr className="border-b border-line last:border-b-0">
      <td
        className="px-3 py-1.5 text-11 text-ink-3"
        title={new Date(item.receivedAt).toLocaleString()}
      >
        {formatRelativeTime(item.receivedAt)}
      </td>
      <td className="min-w-0 py-1.5 pr-3">
        <button
          type="button"
          className="block max-w-full cursor-pointer truncate text-left font-mono text-11 text-ink hover:underline"
          title={`Preview ${item.fileName}`}
          onClick={() => onPreview(item)}
        >
          {item.fileName}
        </button>
      </td>
      <td className="py-1.5 pr-3 text-11 text-ink-3">{formatBytes(item.bytes)}</td>
      <td className="min-w-0 py-1.5 pr-3">
        {LINKED_PILL_STATUSES.has(item.status) && item.jobId ? (
          <Link
            to={tabularJobPath(item.jobId)}
            state={{ from: backTo }}
            title={status.tooltip}
            className="inline-flex max-w-full"
          >
            <StatusPill status={status.status}>{status.label}</StatusPill>
          </Link>
        ) : (
          <span title={status.tooltip} className="inline-flex max-w-full">
            <StatusPill status={status.status}>{status.label}</StatusPill>
          </span>
        )}
        {item.status === 'failed' && item.error ? (
          <div
            className={`mt-0.5 truncate text-11 ${item.acknowledged ? 'text-ink-3' : 'text-st-down'}`}
            title={item.error}
          >
            {item.error}
          </div>
        ) : null}
      </td>
      <td className="py-1.5 pr-3 text-11">
        {item.jobId ? (
          <Link
            to={tabularJobPath(item.jobId)}
            state={{ from: backTo }}
            className="inline-flex items-center gap-1 font-mono text-st-info hover:underline"
          >
            {shortId(item.jobId)}
            <ArrowUpRight className="size-3" aria-hidden />
          </Link>
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </td>
      <td className="min-w-0 py-1.5 pr-3">
        <span
          className="block truncate font-mono text-11 text-ink-3"
          title={item.principalUserId || undefined}
        >
          {item.principalUserId || '—'}
        </span>
      </td>
      <td className="py-1 pr-2 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              disabled={busy}
              aria-label={`Actions for ${item.fileName}`}
            >
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <MoreHorizontal className="size-4" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuItem onSelect={() => onDownload(item)}>
              <Download />
              Download
            </DropdownMenuItem>
            {runLabel ? (
              <DropdownMenuItem disabled={olderPending !== undefined} onSelect={() => onRun(item)}>
                {item.status === 'pending' ? <Play /> : <RotateCcw />}
                <span className="flex min-w-0 flex-col">
                  {runLabel}
                  {olderPending ? (
                    <span className="truncate text-11 text-ink-3">
                      Run or skip {olderPending.fileName} first
                    </span>
                  ) : null}
                </span>
              </DropdownMenuItem>
            ) : null}
            {held ? (
              <DropdownMenuItem onSelect={() => onConfirm({ action: 'replace', item })}>
                <ArrowRightLeft />
                Replace earlier rows…
              </DropdownMenuItem>
            ) : null}
            {canSkip ? (
              <DropdownMenuItem onSelect={() => onConfirm({ action: 'skip', item })}>
                {held ? <CircleX /> : <SkipForward />}
                {held ? 'Discard…' : 'Skip…'}
              </DropdownMenuItem>
            ) : null}
            {canIgnore ? (
              <DropdownMenuItem onSelect={() => onIgnore(item)}>
                <Ban />
                Ignore bad file…
              </DropdownMenuItem>
            ) : null}
            {canCancel ? (
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => onConfirm({ action: 'cancel', item })}
              >
                <Square />
                Cancel run…
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  )
}

function ConfirmRowActionDialog({
  confirm,
  busy,
  onOpenChange,
  onConfirm
}: {
  confirm: PendingConfirm | null
  busy: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (confirm: PendingConfirm) => void
}) {
  const copy = confirm ? confirmCopy(confirm) : null
  return (
    <AlertDialog open={confirm !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="break-all">{copy?.title}</AlertDialogTitle>
          <AlertDialogDescription>{copy?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{copy?.keep}</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={busy}
            onClick={(event) => {
              event.preventDefault()
              if (confirm) onConfirm(confirm)
            }}
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {copy?.action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function confirmCopy({ action, item }: PendingConfirm): {
  title: string
  description: string
  keep: string
  action: string
} {
  if (action === 'replace') {
    return {
      title: `Replace the rows of the earlier ${item.fileName}?`,
      description:
        'An earlier file of this config has the same name. Running this file deletes the rows that file wrote and writes this file’s rows in their place. Discard it instead if the name is a coincidence.',
      keep: 'Keep waiting',
      action: 'Replace rows'
    }
  }
  if (action === 'skip' && item.status === 'awaiting_confirmation') {
    return {
      title: `Discard ${item.fileName}?`,
      description:
        'It will not be processed, and the rows of the earlier file with its name stay as they are. Run anyway later replaces them.',
      keep: 'Keep it',
      action: 'Discard file'
    }
  }
  if (action === 'skip') {
    return {
      title: `Skip ${item.fileName}?`,
      description:
        'It will not be processed. Resending the same file will not queue it again. You can still run it later with Run anyway.',
      keep: 'Keep it',
      action: 'Skip file'
    }
  }
  return {
    title: `Stop processing ${item.fileName}?`,
    description:
      'Tables already written stay written. The file shows as failed and can be run again.',
    keep: 'Keep running',
    action: 'Cancel run'
  }
}

function hasTabularTarget(intake: IngestIntake): boolean {
  return intake.targetKind === 'mapped_tabular_ingest' && intake.target !== null
}

/**
 * Rows arrive newest first. A run is refused while an earlier file is still
 * pending, so each row maps to the oldest loaded pending file before it.
 */
function olderPendingByItem(items: readonly IngestIntakeItem[]): Map<string, IngestIntakeItem> {
  const blockers = new Map<string, IngestIntakeItem>()
  let oldestPending: IngestIntakeItem | undefined
  for (let index = items.length - 1; index >= 0; index--) {
    const item = items[index]
    if (!item) continue
    if (oldestPending) blockers.set(item.itemId, oldestPending)
    else if (item.status === 'pending') oldestPending = item
  }
  return blockers
}

function rowActionError(error: unknown): string {
  const conflict = conflictDetail(error)
  switch (conflict?.code) {
    case 'INTAKE_ITEM_NOT_NEXT':
      return `Run or skip ${conflict.fileName ?? 'the earlier file'} first. This intake processes files in the order they arrived.`
    case 'INTAKE_ITEM_ALREADY_DISPATCHED':
      return 'This file has already started.'
    case 'ITEM_NOT_PENDING':
      return "This file has already started, so it can't be skipped."
    case 'ITEM_NOT_AWAITING_CONFIRMATION':
      return 'This file is no longer waiting for confirmation.'
    case 'INTAKE_ITEM_AWAITING_CONFIRMATION':
      return 'Replace the earlier rows or discard this file first.'
    case 'INTAKE_TARGET_NOT_TABULAR':
      return 'Set a tabular target on this intake first.'
    default:
      return readMutationError(error)
  }
}

function conflictDetail(error: unknown): { code?: string; fileName?: string } | null {
  if (!error || typeof error !== 'object') return null
  const payload = error as { status?: unknown; data?: unknown }
  if (payload.status !== 409 || !payload.data || typeof payload.data !== 'object') return null
  const data = payload.data as {
    error?: unknown
    reason?: unknown
    fileName?: unknown
  }
  const code =
    typeof data.error === 'string'
      ? data.error
      : typeof data.reason === 'string'
        ? data.reason
        : undefined
  return {
    code,
    fileName: typeof data.fileName === 'string' ? data.fileName : undefined
  }
}

function shortId(value: string): string {
  return value.length > 12 ? value.slice(0, 8) : value
}
